'use client'

import { createAlertDialog } from '@gluestack-ui/alert-dialog'
import type { VariantProps } from '@gluestack-ui/nativewind-utils'
import { tva } from '@gluestack-ui/nativewind-utils/tva'
import { useStyleContext, withStyleContext } from '@gluestack-ui/nativewind-utils/withStyleContext'
import { AnimatePresence, createMotionAnimatedComponent, Motion } from '@legendapp/motion'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Pressable, ScrollView, View } from 'react-native'

const AnimatedPressable = createMotionAnimatedComponent(Pressable)

const SCOPE = 'ALERT_DIALOG'

const UIAccessibleAlertDialog = createAlertDialog({
  // @ts-ignore
  Root: withStyleContext(View, SCOPE),
  Body: ScrollView,
  // @ts-ignore
  Content: Motion.View,
  CloseButton: Pressable,
  Header: View,
  Footer: View,
  // @ts-ignore
  Backdrop: AnimatedPressable,
  AnimatePresence: AnimatePresence
})

// @ts-ignore
cssInterop(Motion.View, { className: 'style' })
// @ts-ignore
cssInterop(AnimatedPressable, { className: 'style' })

const alertDialogStyle = tva({
  base: 'group/modal h-full w-full items-center justify-center web:pointer-events-none',
  parentVariants: {
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
      full: ''
    }
  }
})

const alertDialogContentStyle = tva({
  base: 'gap-4 overflow-hidden rounded-lg border border-outline-100 bg-background-0 p-4',
  parentVariants: {
    size: {
      xs: 'w-[60%] max-w-[360px]',
      sm: 'w-[70%] max-w-[420px]',
      md: 'w-[80%] max-w-[510px]',
      lg: 'w-[90%] max-w-[640px]',
      full: 'w-full'
    }
  }
})

const alertDialogCloseButtonStyle = tva({
  base: 'group/alert-dialog-close-button z-10 rounded-sm p-2 outline-0 web:cursor-pointer data-[focus-visible=true]:bg-background-100'
})

const alertDialogHeaderStyle = tva({
  base: 'flex-row items-center justify-between'
})

const alertDialogFooterStyle = tva({
  base: 'flex-row items-center justify-between gap-2'
})

const alertDialogBodyStyle = tva({ base: '' })

const alertDialogBackdropStyle = tva({
  base: 'absolute bottom-0 left-0 right-0 top-0 bg-background-dark web:cursor-default'
})

export type AlertDialogProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog> &
  VariantProps<typeof alertDialogStyle>

export type AlertDialogContentProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.Content> &
  VariantProps<typeof alertDialogContentStyle> & { className?: string }

export type AlertDialogCloseButtonProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.CloseButton> &
  VariantProps<typeof alertDialogCloseButtonStyle>

export type AlertDialogHeaderProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.Header> &
  VariantProps<typeof alertDialogHeaderStyle>

export type AlertDialogFooterProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.Footer> &
  VariantProps<typeof alertDialogFooterStyle>

export type AlertDialogBodyProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.Body> &
  VariantProps<typeof alertDialogBodyStyle>

export type AlertDialogBackdropProps = React.ComponentPropsWithoutRef<typeof UIAccessibleAlertDialog.Backdrop> &
  VariantProps<typeof alertDialogBackdropStyle> & { className?: string }

const AlertDialog = React.forwardRef<React.ComponentRef<typeof UIAccessibleAlertDialog>, AlertDialogProps>(
  ({ className, size = 'md', ...props }, ref) => {
    return (
      <UIAccessibleAlertDialog
        ref={ref}
        {...props}
        // @ts-ignore
        className={alertDialogStyle({ class: className })}
        context={{ size }}
        pointerEvents='box-none'
      />
    )
  }
)

const AlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof UIAccessibleAlertDialog.Content>,
  AlertDialogContentProps
>(({ accessibilityLiveRegion = 'assertive', accessibilityRole = 'alert', className, size, ...props }, ref) => {
  const { size: parentSize } = useStyleContext(SCOPE)

  return (
    <UIAccessibleAlertDialog.Content
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityRole={accessibilityRole}
      pointerEvents='auto'
      ref={ref}
      initial={{
        scale: 0.9,
        opacity: 0
      }}
      animate={{
        scale: 1,
        opacity: 1
      }}
      exit={{
        scale: 0.9,
        opacity: 0
      }}
      transition={{
        type: 'spring',
        damping: 18,
        stiffness: 250,
        opacity: {
          type: 'timing',
          duration: 250
        }
      }}
      {...props}
      className={alertDialogContentStyle({
        parentVariants: {
          size: parentSize
        },
        size,
        class: className
      })}
    />
  )
})

const AlertDialogCloseButton = React.forwardRef<
  React.ComponentRef<typeof UIAccessibleAlertDialog.CloseButton>,
  AlertDialogCloseButtonProps
>(({ className, ...props }, ref) => {
  return (
    <UIAccessibleAlertDialog.CloseButton
      ref={ref}
      {...props}
      className={alertDialogCloseButtonStyle({
        class: className
      })}
    />
  )
})

const AlertDialogHeader = React.forwardRef<
  React.ComponentRef<typeof UIAccessibleAlertDialog.Header>,
  AlertDialogHeaderProps
>(({ className, ...props }, ref) => {
  return (
    <UIAccessibleAlertDialog.Header
      accessible
      accessibilityRole='header'
      focusable
      importantForAccessibility='yes'
      ref={ref}
      tabIndex={0}
      {...props}
      className={alertDialogHeaderStyle({
        class: className
      })}
    />
  )
})

const AlertDialogFooter = React.forwardRef<
  React.ComponentRef<typeof UIAccessibleAlertDialog.Footer>,
  AlertDialogFooterProps
>(({ className, ...props }, ref) => {
  return (
    <UIAccessibleAlertDialog.Footer
      ref={ref}
      {...props}
      className={alertDialogFooterStyle({
        class: className
      })}
    />
  )
})

const AlertDialogBody = React.forwardRef<React.ComponentRef<typeof UIAccessibleAlertDialog.Body>, AlertDialogBodyProps>(
  ({ className, ...props }, ref) => {
    return (
      <UIAccessibleAlertDialog.Body
        bounces={false}
        ref={ref}
        tabIndex={0}
        {...props}
        className={alertDialogBodyStyle({
          class: className
        })}
      />
    )
  }
)

const AlertDialogBackdrop = React.forwardRef<
  React.ComponentRef<typeof UIAccessibleAlertDialog.Backdrop>,
  AlertDialogBackdropProps
>(({ className, ...props }, ref) => {
  return (
    <UIAccessibleAlertDialog.Backdrop
      accessibilityElementsHidden
      aria-hidden
      importantForAccessibility='no-hide-descendants'
      ref={ref}
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 0.5
      }}
      exit={{
        opacity: 0
      }}
      transition={{
        type: 'spring',
        damping: 18,
        stiffness: 250,
        opacity: {
          type: 'timing',
          duration: 250
        }
      }}
      {...props}
      className={alertDialogBackdropStyle({
        class: className
      })}
    />
  )
})

AlertDialog.displayName = 'AlertDialog'
AlertDialogContent.displayName = 'AlertDialogContent'
AlertDialogCloseButton.displayName = 'AlertDialogCloseButton'
AlertDialogHeader.displayName = 'AlertDialogHeader'
AlertDialogFooter.displayName = 'AlertDialogFooter'
AlertDialogBody.displayName = 'AlertDialogBody'
AlertDialogBackdrop.displayName = 'AlertDialogBackdrop'

export {
  AlertDialog,
  AlertDialogContent,
  AlertDialogCloseButton,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogBody,
  AlertDialogBackdrop
}
